import LanguageContext from "components/LanguageContext";
import PropTypes from "prop-types";
import React, { forwardRef, useCallback, useContext, useImperativeHandle, useRef } from "react";
import { createPortal } from "react-dom";
import t from "utils/locales/translation.json";

const DSFRModal = forwardRef((props, ref) => {
  const { children = null, onClose, size = { xs: 12, sm: 12, md: 12, lg: 12 }, top = false } = props;
  const { language } = useContext(LanguageContext);
  const modalRef = useRef(null);

  const gridClass = React.useMemo(() => {
    return Object.entries(size).reduce((a, b) => {
      return a.concat(` fr-col-${b[0]}-${b[1]}`);
    }, "fr-col-12");
  }, [size]);

  const openModal = useCallback(() => {
    if (typeof window !== "undefined") {
      window.dsfr(modalRef.current)?.modal?.disclose();
    }
  }, [modalRef]);

  const closeModal = useCallback(() => {
    if (typeof window !== "undefined") {
      window.dsfr(modalRef.current)?.modal?.conceal();
    }
  }, [modalRef]);

  const isModalOpen = useCallback(() => {
    if (typeof window !== "undefined") {
      return window.dsfr(modalRef.current)?.modal?.isDisclosed;
    }
    return false;
  }, [modalRef]);

  useImperativeHandle(
    ref,
    () => ({
      openModal: openModal,
      closeModal: closeModal,
      isModalOpen: isModalOpen,
    }),
    [openModal, closeModal, isModalOpen],
  );

  if (typeof window === "undefined") {
    return null;
  }

  // we don't return null when the modal is closed because it always need to be in DOM cause of DSFR
  return createPortal(
    <dialog
      aria-labelledby="fr-modal-title"
      id="fr-modal"
      className={`fr-modal${top ? " fr-modal--top" : ""}`}
      tabIndex={0}
      ref={modalRef}
    >
      <div className={`fr-container fr-container--fluid fr-container-md${top ? " fr-mt-4w" : ""}`}>
        <div className="fr-grid-row fr-grid-row--center">
          <div className={gridClass}>
            <div className="fr-modal__body">
              <div className="fr-modal__header">
                <button
                  type="button"
                  className="fr-link--close fr-link"
                  title={t[language].components.close_modal_icon_title}
                  onClick={onClose}
                >
                  {t[language].common.close}
                </button>
              </div>
              {children}
            </div>
          </div>
        </div>
      </div>
    </dialog>,
    document.body,
  );
});

DSFRModal.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  size: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
  }),
  top: PropTypes.bool,
};

export default DSFRModal;
